import { ConfigurationList, ConfigurationPaper } from "../configuration-items";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AppShortcutIcon from "@mui/icons-material/AppShortcut";
import { MfaQrCodeEnroll } from "./MfaQrCodeEnroll";
import useMultifactorAuthSettings from "./hooks/useMultifactorAuthSettings";
import MfaActionsButton from "./components/MfaActionsButton";
import { visuallyHidden } from "@mui/utils";

export default function MultifactorAuthentication() {
  const {
    accordion,
    state,
    user,
    signInMethod,
    handleAddTotp,
    handleVerifyTotp,
    handleCancelEnrollment,
    handleRemoveTotp,
    handleVerifyTotpForUnenroll,
    enrolledInMFA,
    password,
    setPassword,
    mfaData,
    verificationCode,
    setVerificationCode,
  } = useMultifactorAuthSettings();

  return (
    <ConfigurationPaper>
      <ConfigurationList title="Two-factor methods">
        <Grid container spacing={2} sx={{ py: 0, px: 2 }}>
          <Grid
            container
            size={12}
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Accordion
              expanded={accordion.open}
              disableGutters={true}
              square={true}
              sx={{
                width: "100%",
                "&.MuiAccordion-root": {
                  borderTop: "none",
                },
              }}
            >
              <AccordionSummary
                id="multifactor-authentiation-panel-header"
                aria-controls="multifactor-authentication-panel-content"
                sx={{ px: 0 }}
              >
                <Grid container size={12}>
                  <Grid size="grow">
                    <Stack direction="row">
                      <AppShortcutIcon />
                      <Stack>
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          sx={{ ml: 2 }}
                        >
                          Authenticator App{" "}
                          <Chip
                            label="enabled"
                            variant="outlined"
                            size="small"
                            color="success"
                            sx={{
                              ml: 1,
                              ...(enrolledInMFA ? {} : { display: "none" }),
                            }}
                          />
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 2 }}
                        >
                          Use an authentication app or browser extension to
                          generate one-time codes.
                        </Typography>
                      </Stack>
                    </Stack>
                  </Grid>
                  <Grid size="auto">
                    <MfaActionsButton
                      {...{
                        status: state.status,
                        handleAddTotp,
                        handleCancelEnrollment,
                        handleRemoveTotp,
                        enrolledInMFA,
                        mfaData,
                      }}
                    />
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails sx={{ mb: 2 }}>
                <MfaQrCodeEnroll
                  state={state}
                  pending={Boolean(state.status === "pending")}
                  qrCodeUrl={mfaData?.qrCodeUrl}
                  manualCode={mfaData?.secret.secretKey}
                  verificationCode={verificationCode}
                  setVerificationCode={setVerificationCode}
                  handleAddTotp={handleAddTotp}
                  handleVerifyTotp={handleVerifyTotp}
                  // password signin method
                  signInMethod={signInMethod}
                  password={password}
                  setPassword={setPassword}
                  user={user}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>

          {(state.status === "mfa required" ||
            state.status === "mfa verifying") && (
            <Grid
              container
              size={12}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                p: 2,
              }}
            >
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body1">
                  For your security, please verify with your authenticator app
                  to complete the removal of MFA.
                </Typography>
                <Box
                  component="form"
                  onSubmit={handleVerifyTotpForUnenroll}
                  sx={{ mt: 2 }}
                >
                  <Stack direction="row" gap={2}>
                    <FormControl fullWidth>
                      <InputLabel
                        htmlFor="verification-code"
                        sx={visuallyHidden}
                      >
                        Verification Code
                      </InputLabel>
                      <TextField
                        type="text"
                        autoComplete="one-time-code"
                        fullWidth
                        id="verification-code"
                        label="Verification Code"
                        aria-describedby="verification-code-error"
                        onChange={(e) => setVerificationCode(e.target.value)}
                        sx={{
                          borderRadius: 1,
                        }}
                        error={Boolean(state.error)}
                      />
                    </FormControl>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      loading={state.status === "mfa verifying"}
                      loadingPosition="start"
                      sx={{
                        alignSelf: "start",
                        minWidth: 100,
                        height: 55,
                      }}
                    >
                      Verify
                    </Button>
                  </Stack>
                  <FormHelperText
                    error
                    id="verification-code-error"
                    aria-live="polite"
                  >
                    {state.error}
                  </FormHelperText>
                </Box>
              </Grid>
            </Grid>
          )}
        </Grid>
      </ConfigurationList>
    </ConfigurationPaper>
  );
}
